import type { Temperature } from '@shared/types';
import { cn } from '@web/lib/cn';

/**
 * The temperature scale, drawn as a three-segment meter rather than a dot.
 *
 * The count of filled segments carries the value on its own — one, two, three —
 * so the hue is confirmation rather than the message. This is the accent system
 * of the whole console: cold, warm, hot, and nothing else is allowed to be
 * coloured for decoration.
 */

const FILLED: Record<Temperature, number> = { cold: 1, warm: 2, hot: 3 };

const TONE: Record<Temperature, string> = {
  cold: 'bg-cold border-cold',
  warm: 'bg-warm border-warm',
  hot: 'bg-hot border-hot',
};

const EMPTY = 'border-rule-strong';

export function TemperatureMeter({
  temperature,
  className,
}: {
  temperature: Temperature;
  className?: string;
}) {
  const filled = FILLED[temperature];
  return (
    <span
      className={cn('inline-flex items-end gap-[2px]', className)}
      title={`${temperature} lead`}
    >
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          aria-hidden
          className={cn(
            'block w-[3px] border',
            // Rising heights, so the meter reads as a scale even in one colour.
            index === 0 ? 'h-[5px]' : index === 1 ? 'h-[8px]' : 'h-[11px]',
            index < filled ? TONE[temperature] : EMPTY,
          )}
        />
      ))}
      <span className="sr-only">{temperature}</span>
    </span>
  );
}
